import { UserData } from '@beat-venues/common-types';
import { Container, Modal } from '@beat-venues/ui/react';
import { useMutation } from '@tanstack/react-query';
import { PhoneNumberType, PhoneNumberUtil } from 'google-libphonenumber';
import { FormEvent, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Success, ViewStep } from '../../components/welcome-views';
import { useAnalytics } from '../../hooks/use-analytics';
import { putUserData } from '../../services/user-service';
import { getCookie } from '../../utils/cookie-utils';

export interface ViewStateProps {
    welcome: boolean;
    firstName: boolean;
    lastName: boolean;
    phone: boolean;
    success: boolean;
}

export type ActiveStep =
    | 'welcome'
    | 'firstName'
    | 'lastName'
    | 'phone'
    | 'success';

export type NewUserDetail = Pick<
    UserData,
    'lastName' | 'firstName' | 'mobilePhone'
>;

export interface WelcomeFlowProps {
    viewState: ViewStateProps;
    newUserDetail: NewUserDetail;
}

const styleView =
    'flex flex-col justify-between min-h-full md:min-h-auto bg-cover px-8 py-12';

const phoneUtil = PhoneNumberUtil.getInstance();

const isPhoneValid = (phone: string): boolean => {
    if (!phone?.trim()) {
        return true;
    }

    try {
        const parsedPhone = phoneUtil.parseAndKeepRawInput(phone);
        if (!parsedPhone.getNationalNumber()) {
            return true;
        }

        return (
            phoneUtil.isValidNumber(parsedPhone) &&
            phoneUtil.getNumberType(parsedPhone) === PhoneNumberType.MOBILE
        );
    } catch {
        return false;
    }
};

export const Welcome = () => {
    const { t } = useTranslation('');
    const token = getCookie('token');

    const [view, setView] = useState({
        welcome: true,
        firstName: false,
        lastName: false,
        phone: false,
        success: false,
    });

    const [userDetails, setUserDetails] = useState<NewUserDetail>({
        firstName: '',
        lastName: '',
        mobilePhone: '',
    });

    const handleSubmit = (e: FormEvent) => {
        e.preventDefault();
        const tempUserDetails: NewUserDetail = {
            firstName: userDetails.firstName?.trim(),
            lastName: userDetails.lastName?.trim(),
            mobilePhone: userDetails.mobilePhone,
        };
        if (userDetails.mobilePhone && userDetails.mobilePhone.length < 4) {
            tempUserDetails.mobilePhone = '';
        }
        if (token && isPhoneValid(userDetails.mobilePhone || '')) {
            mutation.mutate({ token: token, userDetails: tempUserDetails });
        }
    };

    const mutation = useMutation({
        mutationFn: ({
            token,
            userDetails,
        }: {
            token: string;
            userDetails: NewUserDetail;
        }) => putUserData(token, userDetails),
        onSuccess: () => setView({ ...view, phone: false, success: true }),
    });

    const activeState = Object.keys(view).find(
        (key) => view[key as ActiveStep]
    );

    const { triggerPageView } = useAnalytics();

    useEffect(() => {
        triggerPageView();
    }, []);

    return (
        <div data-portal-root="modal">
            <Modal
                className="w-full bg-gray-900 py-0 md:mx-0 md:my-0 md:max-h-full md:max-w-full md:py-0"
                isShown={true}
                onClose={() => undefined}
            >
                <Modal.Content className="m-auto w-full px-0 pt-0 text-white md:max-h-[924px] md:max-w-[520px] md:px-0 lg:rounded-lg">
                    <Container className="h-full w-full px-0 md:px-0">
                        {!view.success && (
                            <ViewStep
                                activeStep={activeState as ActiveStep}
                                className={styleView}
                                handleSubmit={
                                    activeState === 'phone'
                                        ? handleSubmit
                                        : undefined
                                }
                                isPhoneValid={isPhoneValid(
                                    userDetails.mobilePhone || ''
                                )}
                                setUserDetails={setUserDetails}
                                setView={setView}
                                stepBackground={
                                    activeState as
                                        | 'welcome'
                                        | 'firstName'
                                        | 'lastName'
                                        | 'phone'
                                }
                                subTitle={t(`welcome.${activeState}.subTitle`)}
                                title={
                                    activeState === 'lastName'
                                        ? `${t(
                                              `welcome.${activeState}.title`
                                          )} ${userDetails.firstName}`
                                        : t(`welcome.${activeState}.title`)
                                }
                                userDetails={userDetails}
                                view={view}
                            />
                        )}
                        {view.success && <Success userDetails={userDetails} />}
                    </Container>
                </Modal.Content>
            </Modal>
        </div>
    );
};
